import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "modal", "form",
    "page1", "page2",
    "page1Search", "page2Search",
    "continentTagList", "continentEmpty",
    "countryTagList", "countryEmpty", "countryTotalCount",
    "continentCheckbox", "countryCheckbox",
    "continentRow", "continentAccordionContent",
    "applyButton", "applyCloseButton",
    "hiddenInput",
    "page1Loader", "page2Loader",
    "selectAllButton",
    "continentTagTemplate", "countryTagTemplate"
  ]

  static values = {
    sessionCountryIds: Array,
    searchUrl: String
  }

  // ---- LIFECYCLE ----

  connect() {
    this.checkedCountryIds = new Set()
    this.committedSelectedIds = new Set()
    this.continentMap = new Map()  // continent -> Set<countryId>
    this.countryMeta = new Map()   // countryId -> { name, flag, continent }
    this.holdsScrollLock = false
    this.searchTimer = null
    this.searchAbortController = null

    this.buildMetadataMaps()
    this.indexCheckboxes()
    this.initializeFromSession()
    this.syncCountryCheckboxes()
    this.syncContinentCheckboxes()
    this.renderPage1Tags()
    this.updateApplyButtons()

    if (this.hasModalTarget && !this.modalTarget.classList.contains("hidden")) {
      this.lockScroll()
    }
  }

  disconnect() {
    clearTimeout(this.searchTimer)
    this.searchAbortController?.abort()
    if (this.holdsScrollLock) this.unlockScroll()
  }

  // ---- DATA SETUP ----

  // Full known-country metadata. Built once from the initial render, which is
  // assumed to contain every country. Not rebuilt on search (which renders a
  // filtered subset).
  buildMetadataMaps() {
    this.countryCheckboxTargets.forEach(cb => {
      const id = String(cb.value)
      const continent = cb.dataset.continent
      const label = cb.parentElement
      const name = label?.dataset.countryName || ""
      const flag = label?.dataset.countryFlag || ""

      if (!this.continentMap.has(continent)) this.continentMap.set(continent, new Set())
      this.continentMap.get(continent).add(id)
      this.countryMeta.set(id, { name, flag, continent })
    })
  }

  // DOM lookup caches for currently-rendered checkboxes. Rebuilt after a
  // search re-renders the list.
  indexCheckboxes() {
    this.continentCheckboxByName = new Map()
    this.continentCheckboxTargets.forEach(cb => {
      this.continentCheckboxByName.set(cb.dataset.continent, cb)
    })

    this.countryCheckboxById = new Map()
    this.countryCheckboxesByContinent = new Map()
    this.countryCheckboxTargets.forEach(cb => {
      const continent = cb.dataset.continent
      this.countryCheckboxById.set(String(cb.value), cb)
      if (!this.countryCheckboxesByContinent.has(continent)) {
        this.countryCheckboxesByContinent.set(continent, [])
      }
      this.countryCheckboxesByContinent.get(continent).push(cb)
    })
  }

  initializeFromSession() {
    this.sessionCountryIdsValue.forEach(id => {
      const stringId = String(id)
      this.checkedCountryIds.add(stringId)
      this.committedSelectedIds.add(stringId)
    })
  }

  syncCountryCheckboxes() {
    this.countryCheckboxTargets.forEach(cb => {
      cb.checked = this.checkedCountryIds.has(String(cb.value))
    })
  }

  syncContinentCheckboxes() {
    this.continentMap.forEach((_ids, continent) => {
      this.updateContinentCheckboxState(continent)
    })
  }

  updateContinentCheckboxState(continent) {
    const cb = this.continentCheckboxByName.get(continent)
    if (!cb) return

    const ids = this.continentMap.get(continent)
    if (!ids || ids.size === 0) return

    let selectedCount = 0
    ids.forEach(id => { if (this.checkedCountryIds.has(id)) selectedCount++ })

    if (selectedCount === 0) {
      cb.checked = false
      cb.indeterminate = false
    } else if (selectedCount === ids.size) {
      cb.checked = true
      cb.indeterminate = false
    } else {
      cb.checked = false
      cb.indeterminate = true
    }
  }

  // ---- PAGE NAV / ACCORDION ----

  showPage1() {
    if (this.hasPage2Target) {
      this.page2Target.classList.remove("flex")
      this.page2Target.classList.add("hidden")
    }
    if (this.hasPage1Target) this.page1Target.classList.remove("hidden")
    this.renderPage1Tags()
  }

  showPage2() {
    if (this.hasPage1Target) this.page1Target.classList.add("hidden")
    if (this.hasPage2Target) {
      this.page2Target.classList.remove("hidden")
      this.page2Target.classList.add("flex")
    }

    this.showPage2Loader()
    const hadSearch = this.hasPage2SearchTarget && this.page2SearchTarget.value !== ""
    if (hadSearch) {
      this.page2SearchTarget.value = ""
      this.fetchCountries("").then(result => { if (result !== "aborted") this.afterSearchRender("") })
    } else {
      requestAnimationFrame(() => {
        this.syncContinentCheckboxes()
        this.syncCountryCheckboxes()
        this.hidePage2Loader()
      })
    }
  }

  toggleAccordion(event) {
    const row = event.currentTarget.closest("[data-continent-name]")
    if (!row) return

    const content = row.querySelector("[data-countries-modal-target='continentAccordionContent']")
    if (!content) return

    this.setAccordionOpen(row, content.classList.contains("hidden"))
  }

  setAccordionOpen(row, open) {
    const content = row.querySelector("[data-countries-modal-target='continentAccordionContent']")
    const chevron = row.querySelector("[data-chevron]")
    const toggleBtn = row.querySelector("[data-accordion-toggle]")
    if (content) {
      content.classList.toggle("hidden", !open)
      row.dataset.open = open ? "true" : "false"
    }
    if (chevron) {
      chevron.style.transform = open ? "rotate(180deg)" : ""
    }
    if (toggleBtn) {
      toggleBtn.setAttribute("aria-expanded", open ? "true" : "false")
    }
  }

  setAllAccordionsOpen(open) {
    this.continentRowTargets.forEach(row => this.setAccordionOpen(row, open))
  }

  // ---- SELECTION ----

  toggleContinentAll(event) {
    const cb = event.currentTarget
    const continent = cb.dataset.continent
    const ids = this.continentMap.get(continent) || new Set()

    cb.indeterminate = false

    ids.forEach(id => {
      if (cb.checked) this.checkedCountryIds.add(id)
      else this.checkedCountryIds.delete(id)
    })

    const visibleBoxes = this.countryCheckboxesByContinent.get(continent) || []
    visibleBoxes.forEach(countryCb => { countryCb.checked = cb.checked })

    this.updateApplyButtons()
  }

  toggleCountry(event) {
    const cb = event.currentTarget
    const id = String(cb.value)

    if (cb.checked) this.checkedCountryIds.add(id)
    else this.checkedCountryIds.delete(id)

    this.updateContinentCheckboxState(cb.dataset.continent)
    this.updateApplyButtons()
  }

  selectAllCountries() {
    this.countryCheckboxTargets.forEach(cb => {
      this.checkedCountryIds.add(String(cb.value))
      cb.checked = true
    })
    this.syncContinentCheckboxes()
    this.updateApplyButtons()
  }

  // ---- PAGE 1 TAGS ----

  renderPage1Tags() {
    this.renderContinentTags(this.getFullySelectedContinents())
    this.renderCountryTags(this.getAllSelectedCountries())

    if (this.hasCountryTotalCountTarget) {
      this.countryTotalCountTarget.textContent = this.checkedCountryIds.size
    }

    if (this.hasPage1SearchTarget) {
      this.filterPage1Tags(this.page1SearchTarget.value.toLowerCase().trim())
    }
  }

  renderContinentTags(continents) {
    if (!this.hasContinentTagListTarget) return

    const fragment = document.createDocumentFragment()
    continents.forEach(continent => {
      const node = this.cloneTemplate(this.continentTagTemplateTarget)
      node.querySelector("[data-tag-label]").textContent = continent
      const btn = node.querySelector("[data-tag-remove]")
      btn.dataset.continent = continent
      btn.setAttribute("aria-label", `Remove ${continent}`)
      fragment.appendChild(node)
    })
    this.continentTagListTarget.replaceChildren(fragment)

    if (this.hasContinentEmptyTarget) {
      this.continentEmptyTarget.classList.toggle("hidden", continents.length > 0)
    }
  }

  renderCountryTags(countries) {
    if (!this.hasCountryTagListTarget) return

    const fragment = document.createDocumentFragment()
    countries.forEach(c => {
      const node = this.cloneTemplate(this.countryTagTemplateTarget)
      node.querySelector("[data-tag-flag]").textContent = c.flag
      node.querySelector("[data-tag-label]").textContent = c.name
      const btn = node.querySelector("[data-tag-remove]")
      btn.dataset.countryId = c.id
      btn.setAttribute("aria-label", `Remove ${c.name}`)
      fragment.appendChild(node)
    })
    this.countryTagListTarget.replaceChildren(fragment)

    if (this.hasCountryEmptyTarget) {
      this.countryEmptyTarget.classList.toggle("hidden", countries.length > 0)
    }
  }

  cloneTemplate(template) {
    return template.content.firstElementChild.cloneNode(true)
  }

  getFullySelectedContinents() {
    const result = []
    this.continentMap.forEach((ids, continent) => {
      if (ids.size === 0) return
      let allSelected = true
      for (const id of ids) {
        if (!this.checkedCountryIds.has(id)) { allSelected = false; break }
      }
      if (allSelected) result.push(continent)
    })
    return result
  }

  getAllSelectedCountries() {
    const result = []
    this.checkedCountryIds.forEach(id => {
      const meta = this.countryMeta.get(id)
      if (meta) result.push({ id, ...meta })
    })
    return result
  }

  // ---- TAG REMOVAL ----

  removeContinent(event) {
    const continent = event.currentTarget.dataset.continent
    const ids = this.continentMap.get(continent) || new Set()
    ids.forEach(id => this.checkedCountryIds.delete(id))

    const visibleBoxes = this.countryCheckboxesByContinent.get(continent) || []
    visibleBoxes.forEach(cb => { cb.checked = false })
    this.updateContinentCheckboxState(continent)

    this.renderPage1Tags()
    this.updateApplyButtons()
  }

  removeCountry(event) {
    const id = String(event.currentTarget.dataset.countryId)
    this.checkedCountryIds.delete(id)

    const cb = this.countryCheckboxById.get(id)
    if (cb) cb.checked = false

    const meta = this.countryMeta.get(id)
    if (meta) this.updateContinentCheckboxState(meta.continent)

    this.renderPage1Tags()
    this.updateApplyButtons()
  }

  // ---- SEARCH ----

  searchPage1(event) {
    const term = event.target.value.toLowerCase().trim()
    this.showPage1Loader()
    clearTimeout(this.searchTimer)
    this.searchTimer = setTimeout(() => {
      this.filterPage1Tags(term)
      this.hidePage1Loader()
    }, 200)
  }

  filterPage1Tags(term) {
    const filter = (list) => {
      Array.from(list.children).forEach(tag => {
        tag.classList.toggle("hidden", term !== "" && !tag.textContent.toLowerCase().includes(term))
      })
    }
    if (this.hasContinentTagListTarget) filter(this.continentTagListTarget)
    if (this.hasCountryTagListTarget) filter(this.countryTagListTarget)
  }

  searchPage2(event) {
    const term = event.target.value.trim()
    this.showPage2Loader()
    clearTimeout(this.searchTimer)
    this.searchTimer = setTimeout(async () => {
      const result = await this.fetchCountries(term)
      // Skip rendering on abort — a newer request is already in flight and
      // will run afterSearchRender (which also hides the loader).
      if (result !== "aborted") this.afterSearchRender(term)
    }, 300)
  }

  afterSearchRender(term) {
    this.indexCheckboxes()
    this.syncCountryCheckboxes()
    this.syncContinentCheckboxes()
    this.setAllAccordionsOpen(Boolean(term))
    this.updateSelectAllButton(term)
    this.hidePage2Loader()
  }

  updateSelectAllButton(term) {
    if (!this.hasSelectAllButtonTarget) return
    this.selectAllButtonTarget.textContent = term ? "Select Visible" : "Select All Countries"
  }

  async fetchCountries(term) {
    this.searchAbortController?.abort()
    const controller = new AbortController()
    this.searchAbortController = controller

    const params = new URLSearchParams({ q: term })
    try {
      const response = await fetch(`${this.searchUrlValue}?${params.toString()}`, {
        headers: {
          "Accept": "text/vnd.turbo-stream.html",
          "X-Requested-With": "XMLHttpRequest"
        },
        signal: controller.signal
      })
      if (!response.ok) throw new Error(`Search failed: ${response.status}`)
      const body = await response.text()
      if (controller.signal.aborted) return "aborted"
      Turbo.renderStreamMessage(body)
      // Yield a frame so Stimulus can re-register targets from the new DOM.
      await new Promise(resolve => requestAnimationFrame(resolve))
      return "rendered"
    } catch (err) {
      if (err.name === "AbortError") return "aborted"
      console.error("Country search error:", err)
      return "error"
    } finally {
      if (this.searchAbortController === controller) this.searchAbortController = null
    }
  }

  // ---- APPLY / CLOSE / OPEN ----

  updateApplyButtons() {
    const empty = this.checkedCountryIds.size === 0
    if (this.hasApplyButtonTarget) {
      this.applyButtonTarget.textContent = empty ? "Show All Countries" : "Apply"
    }
    if (this.hasApplyCloseButtonTarget) {
      this.applyCloseButtonTarget.textContent = empty ? "Show All Countries" : "Apply & Close"
    }
  }

  populateHiddenInputs() {
    if (!this.hasFormTarget) return
    this.hiddenInputTargets.forEach(input => input.remove())

    const fragment = document.createDocumentFragment()
    this.checkedCountryIds.forEach(id => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "country_ids[]"
      input.value = id
      input.dataset.countriesModalTarget = "hiddenInput"
      fragment.appendChild(input)
    })
    this.formTarget.appendChild(fragment)

    this.committedSelectedIds = new Set(this.checkedCountryIds)
  }

  ensureSelection() {
    if (this.checkedCountryIds.size === 0) this.selectAllCountries()
  }

  applyAndClose() {
    this.ensureSelection()
    if (this.hasFormTarget) this.formTarget.requestSubmit()
  }

  validate() {
    this.ensureSelection()
    this.populateHiddenInputs()
  }

  close() {
    this.checkedCountryIds = new Set(this.committedSelectedIds)
    this.syncCountryCheckboxes()
    this.syncContinentCheckboxes()
    this.renderPage1Tags()
    this.updateApplyButtons()
    this.showPage1()
    this.hideModal()
  }

  closeOnEscape(event) {
    if (this.hasModalTarget && !this.modalTarget.classList.contains("hidden")) {
      event.preventDefault()
      this.close()
    }
  }

  toggle() {
    if (!this.hasModalTarget) return
    if (this.modalTarget.classList.contains("hidden")) this.showModal()
    else this.hideModal()
  }

  showModal() {
    if (!this.hasModalTarget) return
    this.showPage1()
    this.modalTarget.classList.remove("hidden")
    this.modalTarget.style.display = "flex"
    this.lockScroll()
  }

  hideModal() {
    if (!this.hasModalTarget) return
    this.modalTarget.classList.add("hidden")
    this.modalTarget.style.removeProperty("display")
    this.unlockScroll()
  }

  // ---- LOADERS ----

  showPage1Loader() { if (this.hasPage1LoaderTarget) this.page1LoaderTarget.classList.remove("hidden") }
  hidePage1Loader() { if (this.hasPage1LoaderTarget) this.page1LoaderTarget.classList.add("hidden") }
  showPage2Loader() { if (this.hasPage2LoaderTarget) this.page2LoaderTarget.classList.remove("hidden") }
  hidePage2Loader() { if (this.hasPage2LoaderTarget) this.page2LoaderTarget.classList.add("hidden") }

  // ---- SCROLL LOCK ----
  // Reference-counted on document.documentElement so concurrent modals don't
  // clobber each other. holdsScrollLock makes disconnect() idempotent — we
  // only decrement if THIS controller was the one to lock.

  lockScroll() {
    if (this.holdsScrollLock) return
    const el = document.documentElement
    el.dataset.scrollLockCount = String(parseInt(el.dataset.scrollLockCount || "0") + 1)
    el.classList.add("overflow-hidden")
    document.body.classList.add("overflow-hidden")
    this.holdsScrollLock = true
  }

  unlockScroll() {
    if (!this.holdsScrollLock) return
    const el = document.documentElement
    const count = Math.max(0, parseInt(el.dataset.scrollLockCount || "0") - 1)
    el.dataset.scrollLockCount = String(count)
    if (count === 0) {
      el.classList.remove("overflow-hidden")
      document.body.classList.remove("overflow-hidden")
    }
    this.holdsScrollLock = false
  }
}
